import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FaEnvelope, FaCheckCircle, FaTimesCircle, FaClock, FaSync, FaFilter, FaCopy, FaEye, FaPaperPlane, FaSearch, FaLock, FaSpinner, FaBan, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import styles from "../ClientManagement.module.css";
import onboardingService from "../../services/onboardingService";
import { useAccessToken } from "../../hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import { useOnboarding, useFormsByPurpose } from "../../hooks";
import { vwClientContactsSummaryService } from "../../services/vwClientContactsSummaryService";
import { contactService } from "../../services/contactService";

const OnboardingRequests = () => {
    const navigate = useNavigate();
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();
    const { createOnboardingLink, loading: onboardingLoading } = useOnboarding();
    const { forms: onboardingForms, loading: formsLoading } = useFormsByPurpose('client_onboarding');
    
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [isNotImplemented, setIsNotImplemented] = useState(false);
    
    // Filters
    const [statusFilter, setStatusFilter] = useState('active'); // 'all', 'active', 'completed', 'expired'
    const [emailFilter, setEmailFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'client_onboarding'
    const [requestStats, setRequestStats] = useState({ total: 0, active: 0, completed: 0, expired: 0 });
    
    // Selected request for details modal
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    
    // Send form modal state
    const [showSendFormModal, setShowSendFormModal] = useState(false);
    const [searchContactQuery, setSearchContactQuery] = useState('');
    const [searchContactResults, setSearchContactResults] = useState([]);
    const [isSearchingContacts, setIsSearchingContacts] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [selectedFormId, setSelectedFormId] = useState('');
    const [sendingForm, setSendingForm] = useState(false);
    
    // Submission data modal state
    const [showSubmissionDataModal, setShowSubmissionDataModal] = useState(false);
    const [submissionData, setSubmissionData] = useState(null);
    const [loadingSubmissionData, setLoadingSubmissionData] = useState(false);
    
    // Cancel token state
    const [cancelingTokenId, setCancelingTokenId] = useState(null);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [requestToCancel, setRequestToCancel] = useState(null);

    useEffect(() => {
        loadInformationRequests();
    }, []); // Load once on mount
    
    // Auto-select default form when forms load
    useEffect(() => {
        if (onboardingForms && onboardingForms.length > 0 && !selectedFormId) {
            const defaultForm = onboardingForms.find(f => f.isDefault);
            setSelectedFormId(defaultForm?.formId || onboardingForms[0].formId);
        }
    }, [onboardingForms, selectedFormId]);

    const loadInformationRequests = useCallback(async () => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            // Always load all requests to ensure statistics are correct
            const filters = {
                includeUsed: true,
                includeExpired: true
            };
            
            // Email filtering is now done client-side for better UX
            
            const data = await onboardingService.getOnboardingRequests(accessToken, filters);
            const requestsData = data || [];
            
            // Debug: Log the first request to see its structure
            if (requestsData.length > 0) {
                console.log('Sample request data:', requestsData[0]);
                console.log('Metadata available:', requestsData[0].metadata);
            }
            
            setRequests(requestsData);
            
            // Calculate statistics
            const now = new Date();
            const stats = {
                total: requestsData.length,
                active: requestsData.filter(r => !r.isConsumed && new Date(r.expiresAt) >= now).length,
                completed: requestsData.filter(r => r.isConsumed).length,
                expired: requestsData.filter(r => !r.isConsumed && new Date(r.expiresAt) < now).length
            };
            setRequestStats(stats);
        } catch (error) {
            console.error('Failed to load information requests:', error);
            
            // Check if this is a "not implemented" error
            if (error.isNotImplemented) {
                setIsNotImplemented(true);
                setErrorAlert(
                    'The information requests feature is not yet available on the backend. ' +
                    'Please contact your system administrator to enable this feature.'
                );
            } else {
                setErrorAlert('Failed to load information requests. Please try again.');
            }
            
            setRequests([]);
            setRequestStats({ total: 0, active: 0, completed: 0, expired: 0 });
        } finally {
            setLoading(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    const handleRefresh = async () => {
        setRefreshing(true);
        await loadInformationRequests();
        setRefreshing(false);
        setInfoAlert('Information requests refreshed');
    };

    const handleViewDetails = async (tokenId) => {
        try {
            const accessToken = await getBackendAccessToken();
            const details = await onboardingService.getOnboardingRequestDetails(accessToken, tokenId);
            
            // Debug: Log the details to see structure
            console.log('Request details:', details);
            console.log('Details metadata:', details.metadata);
            
            setSelectedRequest(details);
            setShowDetailsModal(true);
        } catch (error) {
            console.error('Failed to load request details:', error);
            setErrorAlert('Failed to load request details. Please try again.');
        }
    };

    const handleCopyLink = (url) => {
        navigator.clipboard.writeText(url).then(() => {
            setInfoAlert('Information request link copied to clipboard');
        }).catch(() => {
            setErrorAlert('Failed to copy link to clipboard');
        });
    };
    
    /**
     * Handle canceling/expiring a token - show confirmation dialog
     */
    const handleCancelToken = (request) => {
        setRequestToCancel(request);
        setShowCancelDialog(true);
    };
    
    /**
     * Confirm canceling a token
     */
    const confirmCancelToken = async () => {
        if (!requestToCancel) return;
        
        try {
            setCancelingTokenId(requestToCancel.tokenId);
            const accessToken = await getBackendAccessToken();
            await onboardingService.cancelToken(accessToken, requestToCancel.tokenId);
            
            setInfoAlert(`Information request for ${requestToCancel.recipientEmail} has been canceled`);
            
            // Reload the requests list
            await loadInformationRequests();
        } catch (error) {
            console.error('Failed to cancel token:', error);
            setErrorAlert('Failed to cancel information request. Please try again.');
        } finally {
            setCancelingTokenId(null);
            setShowCancelDialog(false);
            setRequestToCancel(null);
        }
    };
    
    /**
     * Handle viewing submission data
     */
    const handleViewSubmissionData = async (tokenId) => {
        try {
            setLoadingSubmissionData(true);
            const accessToken = await getBackendAccessToken();
            const result = await onboardingService.getSubmissionData(accessToken, tokenId);
            
            if (result.success && result.data) {
                setSubmissionData(result.data);
                setShowSubmissionDataModal(true);
            } else {
                setErrorAlert(result.message || 'No submission data found for this request');
            }
        } catch (error) {
            console.error('Failed to load submission data:', error);
            setErrorAlert('Failed to load submission data. Please try again.');
        } finally {
            setLoadingSubmissionData(false);
        }
    };
    
    /**
     * Handle opening send form modal
     */
    const handleOpenSendFormModal = () => {
        setShowSendFormModal(true);
        setSearchContactQuery('');
        setSearchContactResults([]);
        setSelectedContact(null);
    };
    
    /**
     * Search for contacts
     */
    const handleSearchContacts = useCallback(async (query) => {
        if (!query || query.trim().length < 2) {
            setSearchContactResults([]);
            return;
        }

        try {
            setIsSearchingContacts(true);
            const accessToken = await getBackendAccessToken();
            const searchTerm = query.trim();
            
            // Search by last name and first name
            const lastNameResults = await contactService.searchByLastName(accessToken, searchTerm).catch(() => []);
            const firstNameResults = await contactService.searchByFirstName(accessToken, searchTerm).catch(() => []);
            
            // Combine and deduplicate results
            const combinedResults = [...lastNameResults];
            firstNameResults.forEach(contact => {
                if (!combinedResults.some(c => c.contactId === contact.contactId)) {
                    combinedResults.push(contact);
                }
            });
            
            // If search looks like email, also try email search
            if (searchTerm.includes('@')) {
                try {
                    const emailResults = await contactService.getContactsByEmail(accessToken, searchTerm);
                    emailResults.forEach(contact => {
                        if (!combinedResults.some(c => c.contactId === contact.contactId)) {
                            combinedResults.push(contact);
                        }
                    });
                } catch (emailError) {
                    // Ignore email search errors
                }
            }
            
            setSearchContactResults(combinedResults);
        } catch (error) {
            console.error('Error searching for contacts:', error);
            setErrorAlert('Failed to search for contacts. Please try again.');
            setSearchContactResults([]);
        } finally {
            setIsSearchingContacts(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);
    
    // Debounce contact search
    const debouncedContactSearch = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => handleSearchContacts(value), 300);
            };
        })(),
        [handleSearchContacts]
    );
    
    /**
     * Handle contact selection
     */
    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        setSearchContactQuery('');
        setSearchContactResults([]);
    };
    
    /**
     * Handle sending form to selected contact
     */
    const handleSendForm = async () => {
        if (!selectedContact || !selectedFormId) {
            setErrorAlert('Please select a contact and form');
            return;
        }
        
        if (!selectedContact.email) {
            setErrorAlert('Selected contact does not have an email address');
            return;
        }
        
        try {
            setSendingForm(true);
            
            const informationRequest = {
                formId: selectedFormId,
                clientId: selectedContact.clientId,  // Top-level field for backend tracking
                clientEmail: selectedContact.email,
                clientName: `${selectedContact.firstName} ${selectedContact.lastName}`.trim(),
                prefillData: {
                    contactId: selectedContact.contactId
                }
            };
            
            const response = await createOnboardingLink(informationRequest);
            
            if (response && response.url) {
                setInfoAlert(`Information request sent to ${selectedContact.email}`);
                setShowSendFormModal(false);
                // Refresh the requests list
                await loadInformationRequests();
            } else if (response && response.error === 'duplicate_onboarding_request') {
                // Backend detected duplicate - show the backend's message
                setErrorAlert(response.message || 'An active onboarding token already exists for this client.');
            } else {
                throw new Error('Failed to create information request link');
            }
        } catch (error) {
            console.error('Failed to send form:', error);
            // Check if error response has a message from backend
            if (error.message && error.message.includes('duplicate_onboarding_request')) {
                setErrorAlert('An active onboarding token already exists for this client. Please wait for it to expire or cancel it before sending a new request.');
            } else {
                setErrorAlert('Failed to send form. Please try again.');
            }
        } finally {
            setSendingForm(false);
        }
    };

    const getStatusBadge = (request) => {
        const now = new Date();
        const expiresAt = new Date(request.expiresAt);
        
        if (request.isConsumed) {
            return (
                <span style={{
                    padding: '0.25rem 0.75rem',
                    background: '#4CAF50',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    borderRadius: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                }}>
                    <FaCheckCircle /> Completed
                </span>
            );
        }
        
        if (expiresAt < now) {
            return (
                <span style={{
                    padding: '0.25rem 0.75rem',
                    background: '#F44336',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    borderRadius: '12px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                }}>
                    <FaTimesCircle /> Expired
                </span>
            );
        }
        
        return (
            <span style={{
                padding: '0.25rem 0.75rem',
                background: '#2196F3',
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                borderRadius: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.25rem'
            }}>
                <FaClock /> Pending
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        // Parse the date string - JavaScript's Date constructor handles ISO 8601 strings (which include UTC times)
        const date = new Date(dateString);
        
        // Check if the date is valid
        if (isNaN(date.getTime())) return 'Invalid Date';
        
        // Convert to PST/PDT using toLocaleString with America/Los_Angeles timezone
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: 'America/Los_Angeles',
            timeZoneName: 'short'
        });
    };

    const getTimeRemaining = (expiresAt) => {
        const now = new Date();
        const expires = new Date(expiresAt);
        const diff = expires - now;
        
        if (diff <= 0) return 'Expired';
        
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (days > 0) {
            return `${days}d ${hours}h remaining`;
        }
        
        if (hours > 0) {
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            return `${hours}h ${minutes}m remaining`;
        }
        
        const minutes = Math.floor(diff / (1000 * 60));
        return `${minutes}m remaining`;
    };

    const formatMetadata = (metadata) => {
        if (!metadata || Object.keys(metadata).length === 0) return null;
        
        const items = [];
        
        if (metadata.userAgent) {
            items.push({ label: 'User Agent', value: metadata.userAgent });
        }
        if (metadata.browser) {
            items.push({ label: 'Browser', value: metadata.browser });
        }
        if (metadata.device) {
            items.push({ label: 'Device', value: metadata.device });
        }
        if (metadata.os) {
            items.push({ label: 'Operating System', value: metadata.os });
        }
        if (metadata.country) {
            items.push({ label: 'Country', value: metadata.country });
        }
        if (metadata.region) {
            items.push({ label: 'Region', value: metadata.region });
        }
        if (metadata.city) {
            items.push({ label: 'City', value: metadata.city });
        }
        if (metadata.timezone) {
            items.push({ label: 'Timezone', value: metadata.timezone });
        }
        if (metadata.language) {
            items.push({ label: 'Language', value: metadata.language });
        }
        
        return items;
    };

    const filteredRequests = requests
        .filter(request => {
            const now = new Date();
            const isExpired = new Date(request.expiresAt) < now;
            
            // Apply email filter
            if (emailFilter.trim()) {
                const searchTerm = emailFilter.toLowerCase();
                const email = (request.recipientEmail || '').toLowerCase();
                if (!email.includes(searchTerm)) return false;
            }
            
            // Apply type filter
            if (typeFilter !== 'all') {
                if (request.purpose !== typeFilter) return false;
            }
            
            // Apply status filter
            if (statusFilter === 'active') {
                if (request.isConsumed || isExpired) return false;
            } else if (statusFilter === 'completed') {
                if (!request.isConsumed) return false;
            } else if (statusFilter === 'expired') {
                if (request.isConsumed || !isExpired) return false;
            }
            // 'all' shows everything
            
            return true;
        })
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // Sort newest to oldest
    
    /**
     * Handle status filter change
     */
    const handleStatusFilterChange = useCallback((filter) => {
        setStatusFilter(filter);
    }, []);
    
    /**
     * Clear all filters
     */
    const clearAllFilters = () => {
        setEmailFilter('');
        setTypeFilter('all');
        setStatusFilter('active');
    };

    const activeRequests = requests.filter(r => !r.isConsumed && new Date(r.expiresAt) >= new Date());
    const completedRequests = requests.filter(r => r.isConsumed);
    const expiredRequests = requests.filter(r => !r.isConsumed && new Date(r.expiresAt) < new Date());

    /**
     * Cancel Confirmation Dialog Component
     */
    const CancelConfirmDialog = () => {
        if (!showCancelDialog || !requestToCancel) return null;

        return (
            <div className={styles.dialogOverlay} onClick={() => setShowCancelDialog(false)}>
                <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.dialogHeader}>
                        <h3>Cancel Information Request</h3>
                        <button
                            className={styles.closeButton}
                            onClick={() => setShowCancelDialog(false)}
                            disabled={cancelingTokenId === requestToCancel.tokenId}
                        >
                            <FaTimes />
                        </button>
                    </div>
                    <div className={styles.dialogBody}>
                        <div className={styles.warningIcon}>
                            <FaExclamationTriangle size={48} color="#e74c3c" />
                        </div>
                        <p className={styles.confirmMessage}>
                            Are you sure you want to cancel this information request? This will immediately expire the link and the recipient will no longer be able to access it.
                        </p>
                        <div className={styles.contactPreview}>
                            <p><strong>Recipient Email:</strong> {requestToCancel.recipientEmail}</p>
                            <p><strong>Created:</strong> {formatDate(requestToCancel.createdAt)}</p>
                            <p><strong>Expires:</strong> {formatDate(requestToCancel.expiresAt)}</p>
                            {requestToCancel.payload?.companyName && (
                                <p><strong>Company:</strong> {requestToCancel.payload.companyName}</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.dialogActions}>
                        <button
                            type="button"
                            className={styles.btnSecondary}
                            onClick={() => setShowCancelDialog(false)}
                            disabled={cancelingTokenId === requestToCancel.tokenId}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.btnDanger}
                            onClick={confirmCancelToken}
                            disabled={cancelingTokenId === requestToCancel.tokenId}
                        >
                            {cancelingTokenId === requestToCancel.tokenId ? 'Canceling...' : 'Cancel Request'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.addClientSection}>
            <CancelConfirmDialog />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2>
                    <FaEnvelope style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                    Information Requests
                </h2>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        onClick={handleOpenSendFormModal}
                        style={{
                            padding: '0.5rem 1rem',
                            background: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        <FaPaperPlane />
                        Send Form to Contact
                    </button>
                    <button
                        onClick={handleRefresh}
                        disabled={refreshing}
                        style={{
                            padding: '0.5rem 1rem',
                            background: 'var(--primary-color)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: refreshing ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        <FaSync className={refreshing ? styles.spinning : ''} />
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem', 
                marginBottom: '2rem' 
            }}>
                <div style={{
                    padding: '1.5rem',
                    background: 'var(--secondary-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px'
                }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2196F3', marginBottom: '0.5rem' }}>
                        {requestStats.active}
                    </div>
                    <div style={{ color: 'var(--text-color)', opacity: 0.7 }}>Active</div>
                </div>
                <div style={{
                    padding: '1.5rem',
                    background: 'var(--secondary-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px'
                }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#4CAF50', marginBottom: '0.5rem' }}>
                        {requestStats.completed}
                    </div>
                    <div style={{ color: 'var(--text-color)', opacity: 0.7 }}>Completed</div>
                </div>
                <div style={{
                    padding: '1.5rem',
                    background: 'var(--secondary-color)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px'
                }}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#F44336', marginBottom: '0.5rem' }}>
                        {requestStats.expired}
                    </div>
                    <div style={{ color: 'var(--text-color)', opacity: 0.7 }}>Expired</div>
                </div>
            </div>

            {/* Filters */}
            <div style={{
                padding: '1rem',
                background: 'var(--secondary-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                marginBottom: '1.5rem'
            }}>
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '1rem',
                    flexWrap: 'wrap'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FaFilter style={{ color: 'var(--text-color)', opacity: 0.7 }} />
                        <span style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>Filters:</span>
                    </div>
                    
                    {/* Type Filter Dropdown */}
                    <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        style={{
                            padding: '0.5rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            background: 'var(--background-color)',
                            color: 'var(--text-color)',
                            cursor: 'pointer'
                        }}
                    >
                        <option value="all">All Types</option>
                        <option value="client_onboarding">Client Onboarding</option>
                    </select>
                    
                    {/* Email Filter Input */}
                    <input
                        type="text"
                        placeholder="Filter by email..."
                        value={emailFilter}
                        onChange={(e) => setEmailFilter(e.target.value)}
                        style={{
                            padding: '0.5rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            flex: '1',
                            minWidth: '200px',
                            maxWidth: '300px'
                        }}
                    />
                    
                    {/* Clear Filters Button */}
                    {(emailFilter || typeFilter !== 'all') && (
                        <button
                            onClick={clearAllFilters}
                            style={{
                                padding: '0.5rem 1rem',
                                background: 'var(--secondary-color)',
                                color: 'var(--text-color)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem'
                            }}
                            title="Clear all filters"
                        >
                            Clear Filters
                        </button>
                    )}
                    
                    {/* Status Filter Buttons */}
                    <div style={{ 
                        display: 'flex', 
                        gap: '0.5rem',
                        marginLeft: 'auto',
                        flexWrap: 'wrap'
                    }}>
                        <button
                            onClick={() => handleStatusFilterChange('all')}
                            style={{
                                padding: '0.5rem 1rem',
                                background: statusFilter === 'all' ? 'var(--primary-color)' : 'var(--secondary-color)',
                                color: statusFilter === 'all' ? 'white' : 'var(--text-color)',
                                border: `1px solid ${statusFilter === 'all' ? 'var(--primary-color)' : 'var(--border-color)'}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: statusFilter === 'all' ? 'bold' : 'normal',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            All ({requestStats.total})
                        </button>
                        <button
                            onClick={() => handleStatusFilterChange('active')}
                            style={{
                                padding: '0.5rem 1rem',
                                background: statusFilter === 'active' ? '#2196F3' : 'var(--secondary-color)',
                                color: statusFilter === 'active' ? 'white' : 'var(--text-color)',
                                border: `1px solid ${statusFilter === 'active' ? '#2196F3' : 'var(--border-color)'}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: statusFilter === 'active' ? 'bold' : 'normal',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Active ({requestStats.active})
                        </button>
                        <button
                            onClick={() => handleStatusFilterChange('completed')}
                            style={{
                                padding: '0.5rem 1rem',
                                background: statusFilter === 'completed' ? '#4CAF50' : 'var(--secondary-color)',
                                color: statusFilter === 'completed' ? 'white' : 'var(--text-color)',
                                border: `1px solid ${statusFilter === 'completed' ? '#4CAF50' : 'var(--border-color)'}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: statusFilter === 'completed' ? 'bold' : 'normal',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Completed ({requestStats.completed})
                        </button>
                        <button
                            onClick={() => handleStatusFilterChange('expired')}
                            style={{
                                padding: '0.5rem 1rem',
                                background: statusFilter === 'expired' ? '#F44336' : 'var(--secondary-color)',
                                color: statusFilter === 'expired' ? 'white' : 'var(--text-color)',
                                border: `1px solid ${statusFilter === 'expired' ? '#F44336' : 'var(--border-color)'}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: statusFilter === 'expired' ? 'bold' : 'normal',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Expired ({requestStats.expired})
                        </button>
                    </div>
                </div>
            </div>

            {/* Requests List */}
            {loading ? (
                <div className={styles.loadingMessage}>
                    <div className={styles.spinner}></div>
                    Loading information requests...
                </div>
            ) : isNotImplemented ? (
                <div style={{
                    padding: '3rem',
                    textAlign: 'center',
                    background: 'var(--secondary-color)',
                    border: '2px dashed var(--border-color)',
                    borderRadius: '8px'
                }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>🚧</div>
                    <h3 style={{ marginBottom: '1rem', color: 'var(--text-color)' }}>Feature Not Available</h3>
                    <p style={{ color: 'var(--text-color)', opacity: 0.7, marginBottom: '1rem' }}>
                        The information requests tracking feature is not yet implemented on the backend server.
                    </p>
                    <p style={{ color: 'var(--text-color)', opacity: 0.7, fontSize: '0.9rem' }}>
                        <strong>What you can do:</strong>
                    </p>
                    <ul style={{ 
                        textAlign: 'left', 
                        display: 'inline-block', 
                        color: 'var(--text-color)', 
                        opacity: 0.7,
                        fontSize: '0.9rem',
                        marginTop: '0.5rem'
                    }}>
                        <li>Contact your system administrator to enable this feature</li>
                        <li>Continue creating information request links from the "Onboard Client" page</li>
                        <li>Check back later for updates</li>
                    </ul>
                    <div style={{ marginTop: '2rem' }}>
                        <button
                            onClick={() => navigate('/clients/onboard')}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'var(--primary-color)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                marginRight: '1rem'
                            }}
                        >
                            Create Information Request
                        </button>
                        <button
                            onClick={() => navigate('/clients')}
                            style={{
                                padding: '0.75rem 1.5rem',
                                background: 'var(--secondary-color)',
                                color: 'var(--text-color)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Back to Dashboard
                        </button>
                    </div>
                </div>
            ) : filteredRequests.length === 0 ? (
                <div className={styles.emptyMessage}>
                    {emailFilter || statusFilter !== 'active' ? (
                        <>
                            <p>No information requests found matching your filter criteria.</p>
                            {emailFilter && <p style={{ fontSize: '0.9rem', opacity: 0.8 }}>Email filter: "{emailFilter}"</p>}
                            <button
                                onClick={clearAllFilters}
                                style={{
                                    marginTop: '1rem',
                                    padding: '0.5rem 1rem',
                                    background: 'var(--primary-color)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Clear Filters
                            </button>
                        </>
                    ) : (
                        <p>No active information requests found.</p>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {filteredRequests.map(request => {
                        const isExpired = new Date(request.expiresAt) < new Date();
                        
                        return (
                            <div
                                key={request.tokenId}
                                style={{
                                    padding: '1.5rem',
                                    background: 'var(--background-color)',
                                    border: `1px solid ${request.isConsumed ? '#4CAF50' : isExpired ? '#F44336' : 'var(--border-color)'}`,
                                    position: 'relative',
                                    borderRadius: '8px',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                            <h3 style={{ margin: 0, color: 'var(--text-color)' }}>
                                                {request.recipientEmail}
                                            </h3>
                                            {getStatusBadge(request)}
                                        </div>
                                        
                                        {/* Client Information - Prominent Display */}
                                        {(request.clientId || request.clientName || request.resourceId) && (
                                            <div style={{ 
                                                marginBottom: '0.75rem',
                                                padding: '0.5rem 0.75rem',
                                                background: 'rgba(76, 175, 80, 0.15)',
                                                border: '2px solid rgba(76, 175, 80, 0.4)',
                                                borderRadius: '6px',
                                                display: 'inline-block'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', color: '#4CAF50' }}>
                                                    <span style={{ fontSize: '1.1rem' }}>🏢</span>
                                                    {request.clientName && (
                                                        <span style={{ fontSize: '0.95rem' }}>{request.clientName}</span>
                                                    )}
                                                    {(request.clientId || request.resourceId) && (
                                                        <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                                                            (ID: {request.clientId || request.resourceId})
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.9rem', opacity: 0.8 }}>
                                            <div>
                                                <strong>Created:</strong> {formatDate(request.createdAt)}
                                            </div>
                                            <div>
                                                <strong>Expires:</strong> {formatDate(request.expiresAt)}
                                                {!request.isConsumed && !isExpired && (
                                                    <span style={{ marginLeft: '0.5rem', color: '#2196F3', fontWeight: 'bold' }}>
                                                        ({getTimeRemaining(request.expiresAt)})
                                                    </span>
                                                )}
                                            </div>
                                            {request.isConsumed && request.consumedAt && (
                                                <div>
                                                    <strong>Completed:</strong> {formatDate(request.consumedAt)}
                                                    {request.consumedFromIp && ` from ${request.consumedFromIp}`}
                                                </div>
                                            )}
                                            {request.payload?.companyName && (
                                                <div>
                                                    <strong>Company:</strong> {request.payload.companyName}
                                                </div>
                                            )}
                                            {request.purpose && (
                                                <div>
                                                    <strong>Purpose:</strong> {request.purpose}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button
                                            onClick={() => handleViewDetails(request.tokenId)}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: 'var(--primary-color)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                fontSize: '0.85rem'
                                            }}
                                        >
                                            <FaEye /> Details
                                        </button>
                                        {!request.isConsumed && !isExpired && (
                                            <>
                                                <button
                                                    onClick={() => handleCopyLink(`${window.location.origin}/client-onboarding/${request.tokenId}`)}
                                                    style={{
                                                        padding: '0.5rem 1rem',
                                                        background: 'var(--secondary-color)',
                                                        color: 'var(--text-color)',
                                                        border: '1px solid var(--border-color)',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    <FaCopy /> Copy Link
                                                </button>
                                                <button
                                                    onClick={() => handleCancelToken(request)}
                                                    disabled={cancelingTokenId === request.tokenId}
                                                    style={{
                                                        padding: '0.5rem 1rem',
                                                        background: '#F44336',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: cancelingTokenId === request.tokenId ? 'not-allowed' : 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        fontSize: '0.85rem',
                                                        opacity: cancelingTokenId === request.tokenId ? 0.6 : 1
                                                    }}
                                                >
                                                    {cancelingTokenId === request.tokenId ? (
                                                        <><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Canceling...</>
                                                    ) : (
                                                        <><FaBan /> Cancel</>
                                                    )}
                                                </button>
                                            </>
                                        )}
                                        {request.isConsumed && request.hasSubmissionData && (
                                            <button
                                                onClick={() => handleViewSubmissionData(request.tokenId)}
                                                disabled={loadingSubmissionData}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: '#FF9800',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: loadingSubmissionData ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    fontSize: '0.85rem',
                                                    opacity: loadingSubmissionData ? 0.6 : 1
                                                }}
                                            >
                                                <FaLock /> {loadingSubmissionData ? 'Loading...' : 'View Data'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Details Modal */}
            {showDetailsModal && selectedRequest && (
                <div className={styles.modal}>
                    <div className={styles.modalContent} style={{ maxWidth: '700px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
                            <h3>Information Request Details</h3>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-color)',
                                    opacity: 0.7
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {/* Client Information - Prominent Display */}
                            {(selectedRequest.clientId || selectedRequest.clientName || selectedRequest.resourceId) && (
                                <div style={{
                                    padding: '1rem',
                                    background: 'rgba(76, 175, 80, 0.15)',
                                    border: '2px solid rgba(76, 175, 80, 0.5)',
                                    borderRadius: '6px'
                                }}>
                                    <div style={{ 
                                        fontSize: '0.9rem', 
                                        fontWeight: 'bold', 
                                        marginBottom: '0.75rem',
                                        color: '#4caf50',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        <span style={{ fontSize: '1.2rem' }}>🏢</span>
                                        Client Information
                                    </div>
                                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                                        {selectedRequest.clientName && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <strong>Client Name:</strong>
                                                <span>{selectedRequest.clientName}</span>
                                            </div>
                                        )}
                                        {(selectedRequest.clientId || selectedRequest.resourceId) && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <strong>Client ID:</strong>
                                                <span>{selectedRequest.clientId || selectedRequest.resourceId}</span>
                                            </div>
                                        )}
                                        {selectedRequest.resourceType && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <strong>Type:</strong>
                                                <span>{selectedRequest.resourceType}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            <div style={{
                                padding: '1rem',
                                background: 'var(--secondary-color)',
                                borderRadius: '6px'
                            }}>
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <strong>Email:</strong> {selectedRequest.recipientEmail}
                                </div>
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <strong>Status:</strong> {getStatusBadge(selectedRequest)}
                                </div>
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <strong>Created:</strong> {formatDate(selectedRequest.createdAt)}
                                </div>
                                <div style={{ marginBottom: '0.5rem' }}>
                                    <strong>Expires:</strong> {formatDate(selectedRequest.expiresAt)}
                                </div>
                                {selectedRequest.purpose && (
                                    <div style={{ marginBottom: '0.5rem' }}>
                                        <strong>Purpose:</strong> {selectedRequest.purpose}
                                    </div>
                                )}
                                {selectedRequest.isConsumed && selectedRequest.consumedAt && (
                                    <>
                                        <div style={{ marginBottom: '0.5rem' }}>
                                            <strong>Completed:</strong> {formatDate(selectedRequest.consumedAt)}
                                        </div>
                                        {selectedRequest.consumedFromIp && (
                                            <div style={{ marginBottom: '0.5rem' }}>
                                                <strong>IP Address:</strong> {selectedRequest.consumedFromIp}
                                            </div>
                                        )}
                                    </>
                                )}
                                {selectedRequest.isConsumed && selectedRequest.hasSubmissionData && (
                                    <div style={{ marginTop: '1rem' }}>
                                        <button
                                            onClick={() => handleViewSubmissionData(selectedRequest.tokenId)}
                                            disabled={loadingSubmissionData}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: '#FF9800',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: loadingSubmissionData ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                width: '100%',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <FaLock /> {loadingSubmissionData ? 'Loading...' : 'View Submission Data'}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {selectedRequest.url && !selectedRequest.isConsumed && (
                                <div style={{
                                    padding: '1rem',
                                    background: 'var(--secondary-color)',
                                    borderRadius: '6px'
                                }}>
                                    <div style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                        Information Request Link:
                                    </div>
                                    <div style={{
                                        padding: '0.75rem',
                                        background: 'var(--background-color)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        wordBreak: 'break-all',
                                        fontSize: '0.85rem',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {selectedRequest.url}
                                    </div>
                                    <button
                                        onClick={() => handleCopyLink(selectedRequest.url)}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: 'var(--primary-color)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        <FaCopy /> Copy Link
                                    </button>
                                </div>
                            )}

                            {selectedRequest.payload && Object.keys(selectedRequest.payload).length > 0 && (
                                <div style={{
                                    padding: '1rem',
                                    background: 'var(--secondary-color)',
                                    borderRadius: '6px'
                                }}>
                                    <div style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                        Prefill Data:
                                    </div>
                                    <pre style={{
                                        padding: '0.75rem',
                                        background: 'var(--background-color)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem',
                                        overflow: 'auto',
                                        maxHeight: '200px'
                                    }}>
                                        {JSON.stringify(selectedRequest.payload, null, 2)}
                                    </pre>
                                </div>
                            )}

                            {selectedRequest.formId && (
                                <div style={{
                                    padding: '1rem',
                                    background: 'var(--secondary-color)',
                                    borderRadius: '6px'
                                }}>
                                    <div>
                                        <strong>Form ID:</strong> {selectedRequest.formId}
                                    </div>
                                </div>
                            )}

                            {selectedRequest.metadata && Object.keys(selectedRequest.metadata).length > 0 && (
                                <div style={{
                                    padding: '1rem',
                                    background: 'var(--secondary-color)',
                                    borderRadius: '6px'
                                }}>
                                    <div style={{ marginBottom: '0.75rem', fontWeight: 'bold', fontSize: '1rem' }}>
                                        📋 Metadata Information
                                    </div>
                                    <div style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                        gap: '0.75rem'
                                    }}>
                                        {formatMetadata(selectedRequest.metadata)?.map((item, index) => (
                                            <div key={index} style={{
                                                padding: '0.5rem',
                                                background: 'var(--background-color)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '4px'
                                            }}>
                                                <div style={{ 
                                                    fontSize: '0.75rem', 
                                                    opacity: 0.7,
                                                    marginBottom: '0.25rem'
                                                }}>
                                                    {item.label}
                                                </div>
                                                <div style={{ 
                                                    fontSize: '0.9rem',
                                                    fontWeight: '500',
                                                    wordBreak: 'break-word'
                                                }}>
                                                    {item.value}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                className={styles.cancelButton}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Send Form Modal */}
            {showSendFormModal && (
                <div className={styles.modal}>
                    <div className={styles.modalContent} style={{ maxWidth: '700px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
                            <h3>
                                <FaPaperPlane style={{ marginRight: '0.5rem' }} />
                                Send Form to Contact
                            </h3>
                            <button
                                onClick={() => setShowSendFormModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-color)',
                                    opacity: 0.7
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* Search and Select Contact */}
                            {!selectedContact ? (
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                        Search for Contact
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="text"
                                            placeholder="Search by name or email..."
                                            value={searchContactQuery}
                                            onChange={(e) => {
                                                setSearchContactQuery(e.target.value);
                                                debouncedContactSearch(e.target.value);
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                paddingLeft: '2.5rem',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '4px',
                                                fontSize: '1rem'
                                            }}
                                        />
                                        <FaSearch style={{
                                            position: 'absolute',
                                            left: '0.75rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            opacity: 0.5
                                        }} />
                                    </div>
                                    {isSearchingContacts && (
                                        <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', opacity: 0.7 }}>
                                            Searching...
                                        </div>
                                    )}
                                    
                                    {/* Search Results */}
                                    {searchContactResults.length > 0 && (
                                        <div style={{ 
                                            marginTop: '1rem',
                                            maxHeight: '300px',
                                            overflowY: 'auto',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '6px',
                                            padding: '0.5rem'
                                        }}>
                                            {searchContactResults.map(contact => (
                                                <button
                                                    key={contact.contactId}
                                                    type="button"
                                                    onClick={() => handleSelectContact(contact)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '1rem',
                                                        marginBottom: '0.5rem',
                                                        background: 'var(--background-color)',
                                                        border: '1px solid var(--border-color)',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        textAlign: 'left',
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.borderColor = 'var(--primary-color)';
                                                        e.currentTarget.style.background = 'var(--secondary-color)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.borderColor = 'var(--border-color)';
                                                        e.currentTarget.style.background = 'var(--background-color)';
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                        <span style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>
                                                            {contact.firstName} {contact.lastName}
                                                        </span>
                                                        <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                                                            {contact.email}
                                                        </span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <>
                                    {/* Selected Contact */}
                                    <div style={{
                                        padding: '1rem',
                                        background: 'var(--secondary-color)',
                                        border: '2px solid var(--primary-color)',
                                        borderRadius: '8px'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div>
                                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                                    Selected Contact
                                                </label>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                    <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                                                        {selectedContact.firstName} {selectedContact.lastName}
                                                    </span>
                                                    <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                                                        {selectedContact.email}
                                                    </span>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => {
                                                    setSelectedContact(null);
                                                }}
                                                style={{
                                                    padding: '0.5rem 1rem',
                                                    background: 'var(--border-color)',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: '0.85rem'
                                                }}
                                            >
                                                Change Contact
                                            </button>
                                        </div>
                                    </div>
                                    
                                    {/* Select Form */}
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                                            Information Request Form <span style={{ color: 'red' }}>*</span>
                                        </label>
                                        {formsLoading ? (
                                            <div style={{ padding: '1rem', textAlign: 'center', opacity: 0.7 }}>
                                                Loading forms...
                                            </div>
                                        ) : onboardingForms && onboardingForms.length > 0 ? (
                                            <>
                                                <select
                                                    value={selectedFormId}
                                                    onChange={(e) => setSelectedFormId(e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem',
                                                        border: '1px solid var(--border-color)',
                                                        borderRadius: '4px',
                                                        fontSize: '1rem'
                                                    }}
                                                >
                                                    {onboardingForms.map(form => (
                                                        <option key={form.formId} value={form.formId}>
                                                            {form.formName} {form.isDefault ? '(Default)' : ''}
                                                        </option>
                                                    ))}
                                                </select>
                                                {selectedFormId && (() => {
                                                    const selectedForm = onboardingForms.find(f => f.formId === selectedFormId);
                                                    return selectedForm?.formDescription ? (
                                                        <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.7 }}>
                                                            {selectedForm.formDescription}
                                                        </div>
                                                    ) : null;
                                                })()}
                                            </>
                                        ) : (
                                            <div style={{ 
                                                padding: '1rem', 
                                                background: '#ffebee',
                                                color: '#c62828',
                                                borderRadius: '6px'
                                            }}>
                                                No information request forms available. Please create one in Forms Management first.
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Warning message during sending */}
                        {sendingForm && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '1rem',
                                background: '#fff3cd',
                                border: '1px solid #ffc107',
                                borderRadius: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                                fontSize: '0.95rem',
                                color: '#856404'
                            }}>
                                <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                                <div>
                                    <strong>⚠️ Please wait - Processing your request...</strong>
                                    <div style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                                        Do not close this page or navigate away. The information request is being created.
                                    </div>
                                </div>
                            </div>
                        )}

                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            <button
                                onClick={() => setShowSendFormModal(false)}
                                className={styles.cancelButton}
                                disabled={sendingForm}
                            >
                                Cancel
                            </button>
                            {selectedContact && (
                                <button
                                    onClick={handleSendForm}
                                    className={styles.submitButton}
                                    disabled={sendingForm || !selectedFormId || onboardingLoading}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                >
                                    {sendingForm || onboardingLoading ? (
                                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                                    ) : (
                                        <FaPaperPlane />
                                    )}
                                    {sendingForm || onboardingLoading ? 'Sending...' : 'Send Form'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Submission Data Modal */}
            {/* Submission Data Modal */}
            {showSubmissionDataModal && submissionData && (
                <div className={styles.modal}>
                    <div className={styles.modalContent} style={{ maxWidth: '800px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <FaLock style={{ color: '#FF9800' }} />
                                Submission Data
                            </h3>
                            <button
                                onClick={() => {
                                    setShowSubmissionDataModal(false);
                                    setSubmissionData(null);
                                }}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-color)',
                                    opacity: 0.7
                                }}
                            >
                                ×
                            </button>
                        </div>
                        
                        <div>
                            {/* Security Notice */}
                            <div style={{
                                padding: '1rem',
                                background: 'rgba(255, 152, 0, 0.15)',
                                border: '1px solid rgba(255, 152, 0, 0.5)',
                                borderRadius: '6px',
                                marginBottom: '1.5rem',
                                fontSize: '0.9rem',
                                color: 'var(--text-color)'
                            }}>
                                <strong style={{ color: '#FF9800' }}>⚠️ Security Notice:</strong> Your access to this sensitive data has been logged for security compliance.
                            </div>
                            
                            {/* Client Information Section - Display clientId and client if available */}
                            {(submissionData.clientId || submissionData.client) && (
                                <div style={{
                                    padding: '1rem',
                                    background: 'rgba(76, 175, 80, 0.1)',
                                    border: '2px solid rgba(76, 175, 80, 0.5)',
                                    borderRadius: '6px',
                                    marginBottom: '1.5rem'
                                }}>
                                    <div style={{ 
                                        fontSize: '0.9rem', 
                                        fontWeight: 'bold', 
                                        marginBottom: '0.75rem',
                                        color: '#4caf50'
                                    }}>
                                        📋 Client Information
                                    </div>
                                    <div style={{ display: 'grid', gap: '0.5rem' }}>
                                        {submissionData.clientId && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <strong>Client ID:</strong>
                                                <span>{submissionData.clientId}</span>
                                            </div>
                                        )}
                                        {submissionData.client && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <strong>Client:</strong>
                                                <span>{submissionData.client}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Submission Metadata Section */}
                            {(submissionData.submittedAt || submissionData.submittedFrom || submissionData.submissionMetadata) && (
                                <div style={{
                                    padding: '1rem',
                                    background: 'rgba(33, 150, 243, 0.1)',
                                    border: '2px solid rgba(33, 150, 243, 0.5)',
                                    borderRadius: '6px',
                                    marginBottom: '1.5rem'
                                }}>
                                    <div style={{ 
                                        fontSize: '0.9rem', 
                                        fontWeight: 'bold', 
                                        marginBottom: '0.75rem',
                                        color: '#2196F3'
                                    }}>
                                        📊 Submission Metadata
                                    </div>
                                    <div style={{ display: 'grid', gap: '0.5rem', fontSize: '0.9rem' }}>
                                        {submissionData.submittedAt && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <strong>Submitted At:</strong>
                                                <span>{formatDate(submissionData.submittedAt)}</span>
                                            </div>
                                        )}
                                        {submissionData.submittedFrom && (
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <strong>IP Address:</strong>
                                                <span>{submissionData.submittedFrom}</span>
                                            </div>
                                        )}
                                        {submissionData.submissionMetadata && (
                                            <>
                                                {submissionData.submissionMetadata.userAgent && (
                                                    <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                                                        <strong>User Agent:</strong>
                                                        <span style={{ fontSize: '0.85rem', opacity: 0.8, wordBreak: 'break-all' }}>
                                                            {submissionData.submissionMetadata.userAgent}
                                                        </span>
                                                    </div>
                                                )}
                                                {submissionData.submissionMetadata.browser && (
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <strong>Browser:</strong>
                                                        <span>{submissionData.submissionMetadata.browser}</span>
                                                    </div>
                                                )}
                                                {submissionData.submissionMetadata.device && (
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <strong>Device:</strong>
                                                        <span>{submissionData.submissionMetadata.device}</span>
                                                    </div>
                                                )}
                                                {submissionData.submissionMetadata.os && (
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <strong>Operating System:</strong>
                                                        <span>{submissionData.submissionMetadata.os}</span>
                                                    </div>
                                                )}
                                                {submissionData.submissionMetadata.country && (
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <strong>Location:</strong>
                                                        <span>
                                                            {submissionData.submissionMetadata.city && `${submissionData.submissionMetadata.city}, `}
                                                            {submissionData.submissionMetadata.region && `${submissionData.submissionMetadata.region}, `}
                                                            {submissionData.submissionMetadata.country}
                                                        </span>
                                                    </div>
                                                )}
                                                {submissionData.submissionMetadata.timezone && (
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <strong>Timezone:</strong>
                                                        <span>{submissionData.submissionMetadata.timezone}</span>
                                                    </div>
                                                )}
                                                {submissionData.submissionMetadata.language && (
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <strong>Language:</strong>
                                                        <span>{submissionData.submissionMetadata.language}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                            
                            {/* Display all submission data fields */}
                            <div style={{ display: 'grid', gap: '1rem' }}>
                                {(() => {
                                    // Flatten step-based data structure
                                    const flattenedData = {};
                                    
                                    Object.entries(submissionData).forEach(([key, value]) => {
                                        // If this is a step key (like "Step2", "Step3", "Stepnull", or numbered like "2", "3")
                                        if ((key.startsWith('Step') || /^\d+$/.test(key)) && typeof value === 'object' && value !== null) {
                                            // Extract the nested data from the step
                                            Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                                                flattenedData[nestedKey] = nestedValue;
                                            });
                                        } else {
                                            // Keep non-step data as-is
                                            flattenedData[key] = value;
                                        }
                                    });
                                    
                                    // System fields to exclude from display
                                    const systemFields = [
                                        'stepNumber',
                                        'stepData',
                                        'markStepComplete',
                                        'moveToNextStep',
                                        'currentStep',
                                        'completedSteps',
                                        'tokenId',
                                        'formId',
                                        'submittedAt',
                                        'submittedFrom',
                                        'submissionMetadata', // Exclude from main display
                                        'clientId',    // Shown in separate Client Information section
                                        'client'       // Shown in separate Client Information section
                                    ];
                                    
                                    // Helper function to format complex values
                                    const formatValue = (value) => {
                                        if (value === null || value === undefined) return 'N/A';
                                        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
                                        if (typeof value === 'string') return value;
                                        if (typeof value === 'number') return String(value);
                                        
                                        // Handle objects
                                        if (typeof value === 'object') {
                                            if (Array.isArray(value)) {
                                                return value.length > 0 ? value.join(', ') : 'None';
                                            }
                                            
                                            // Handle signature object
                                            if (value.name && value.date) {
                                                return `${value.name} (${value.date})`;
                                            }
                                            
                                            // Handle nested objects - format as key: value pairs
                                            const entries = Object.entries(value);
                                            if (entries.length === 0) return 'None';
                                            
                                            return (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                    {entries.map(([k, v]) => {
                                                        // Skip false boolean values in objects
                                                        if (typeof v === 'boolean' && !v) return null;
                                                        
                                                        const label = k
                                                            .replace(/([A-Z])/g, ' $1')
                                                            .replace(/^./, str => str.toUpperCase())
                                                            .trim();
                                                        
                                                        const displayValue = typeof v === 'boolean' ? '✓' : v;
                                                        
                                                        return (
                                                            <div key={k} style={{ paddingLeft: '1rem' }}>
                                                                <strong>{label}:</strong> {String(displayValue)}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        }
                                        
                                        return String(value);
                                    };
                                    
                                    return Object.entries(flattenedData).map(([key, value]) => {
                                        // Skip null/undefined values
                                        if (value === null || value === undefined) return null;
                                        
                                        // Filter out system fields
                                        if (systemFields.includes(key)) return null;
                                        
                                        // Format field name (camelCase to Title Case)
                                        const fieldName = key
                                            .replace(/([A-Z])/g, ' $1')
                                            .replace(/^./, str => str.toUpperCase())
                                            .trim();
                                        
                                        // Identify sensitive fields
                                        const isSensitive = [
                                            'bankAccountNumber', 
                                            'routingNumber', 
                                            'taxId',
                                            'ssn',
                                            'creditCard'
                                        ].some(field => key.toLowerCase().includes(field.toLowerCase()));
                                        return (
                                            <div key={key} style={{
                                                padding: '0.75rem',
                                                background: isSensitive ? 'rgba(239, 83, 80, 0.1)' : 'var(--secondary-color)',
                                                borderRadius: '4px',
                                                border: isSensitive ? '1px solid rgba(239, 83, 80, 0.5)' : '1px solid var(--border-color)'
                                            }}>
                                                <div style={{ 
                                                    fontSize: '0.85rem', 
                                                    opacity: 0.7, 
                                                    marginBottom: '0.25rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.5rem',
                                                    color: 'var(--text-color)'
                                                }}>
                                                    {isSensitive && <FaLock style={{ fontSize: '0.75rem', color: '#ef5350' }} />}
                                                    <strong>{fieldName}</strong>
                                                </div>
                                                <div style={{ 
                                                    wordBreak: 'break-word',
                                                    color: 'var(--text-color)'
                                                }}>
                                                    {formatValue(value)}
                                                </div>
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowSubmissionDataModal(false);
                                    setSubmissionData(null);
                                }}
                                className={styles.cancelButton}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OnboardingRequests;